import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http'
import { Observable, throwError } from 'rxjs'
import { catchError, tap } from 'rxjs/operators'
import { Injectable, inject } from '@angular/core'

import { TraceService } from './trace.service'
import { BODY_LIMITS, redactValue } from './trace-redactor'
import { environment } from '@env/environment'

//* Un GET que tarda más que esto entra a la bitácora aunque haya salido bien: una pantalla que se
//* siente "trabada" casi siempre es esto, y sin el dato el reporte dice "iba lento" y nada más.
const SLOW_GET_MS = 3000

//* Rutas de las que solo se guarda el status. Nunca su URL con parámetros ni nada de su cuerpo.
const CREDENTIAL_PATHS = ['auth/login', 'auth/register', 'auth/forgot-password', 'auth/change-password', 'auth/reset-password', 'verify-ciec']

//* Ruido propio: el envío del reporte y la descarga de sus adjuntos no cuentan nada del problema.
const IGNORED_PATHS = ['worklog/bug-reports', '/attachments/']

/**
 * Registra en la bitácora lo que pasa entre el panel y el servidor.
 *
 * !! Se registra TERCERO en `CoreModule`, después de `JwtInterceptor` y `ErrorInterceptor`. Angular
 * encadena con `reduceRight`, así que la respuesta viaja al revés que la petición: siendo el último
 * registrado, su `catchError` es el PRIMERO en ver un error — antes de que `ErrorInterceptor` haga
 * `logout()` en un 401, que vaciaría la bitácora y se llevaría justo el paso que lo explica.
 *
 * !! Contrapartida de ese orden: para cuando la petición llega aquí ya lleva el `Authorization`
 * puesto. **Este interceptor no lee `request.headers` bajo ninguna circunstancia.**
 *
 * Es además el ÚNICO punto del panel que ve los errores de red: `ApiClass` los convierte en un
 * `next` con `{ error: true, msg: 'Hubo un error de conexión' }` y el componente nunca ve el error.
 */
@Injectable()
export class TraceInterceptor implements HttpInterceptor {
  private readonly trace = inject(TraceService)

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!request.url.startsWith(environment.apiUrl) || IGNORED_PATHS.some((p) => request.url.includes(p))) return next.handle(request)

    const startedAt = Date.now()
    const path = this.normalize(request.url)
    const isCredential = CREDENTIAL_PATHS.some((p) => request.url.includes(p))

    return next.handle(request).pipe(
      tap((event) => {
        if (!(event instanceof HttpResponse)) return
        this.recordSuccess(request, event, path, isCredential, Date.now() - startedAt)
      }),
      catchError((err: HttpErrorResponse) => {
        this.recordFailure(request, err, path, isCredential, Date.now() - startedAt)
        return throwError(() => err)
      })
    )
  }

  /**
   * El cuerpo que se envió, que es donde suele estar el error.
   *
   * Muchos fallos no vienen de la ruta sino de los datos: un plazo imposible, un monto en cero, un
   * activo sin proveedor. Sin esto el reporte dice «POST /quotations falló» y hay que volver a
   * preguntarle a quien reportó — el ping-pong que la herramienta vino a quitar.
   *
   * Va por el redactor como todo lo demás, así que las credenciales no llegan ni aquí. Los datos de
   * negocio SÍ se conservan: es la decisión tomada, y quién puede leerlos lo resuelve el servidor,
   * que recorta los cuerpos para todo el que no sea del equipo ni haya reportado.
   */
  private cuerpoDe(request: HttpRequest<any>): unknown {
    const body = request.body
    if (body === null || body === undefined) return undefined

    //* Un envío de archivos no tiene cuerpo legible. Se anota QUÉ campos llevaba, que para
    //* diagnosticar suele bastar, en vez de un objeto vacío que hace pensar que no se mandó nada.
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      const campos: string[] = []
      body.forEach((_v, k) => { if (!campos.includes(k)) campos.push(k) })
      return `‹formulario: ${campos.join(', ')}›`
    }
    //* Un `redactValue` sobre un Blob devolvería «‹archivo›» sin decir de qué tamaño.
    if (typeof Blob !== 'undefined' && body instanceof Blob) return `‹archivo de ${Math.round(body.size / 1024)} kB›`

    return redactValue(body, BODY_LIMITS)
  }

  /** Quita el prefijo de la API y cambia los identificadores por `:id` para poder agrupar. */
  private normalize(url: string): string {
    const withoutBase = url.replace(environment.apiUrl, '/').split('?')[0]
    return withoutBase
      .split('/')
      .map((seg) => (/^[0-9a-f]{24}$/i.test(seg) || /^\d+$/.test(seg) ? ':id' : seg))
      .join('/')
  }

  private recordSuccess(request: HttpRequest<any>, response: HttpResponse<any>, path: string, isCredential: boolean, ms: number): void {
    if (isCredential) {
      this.trace.push('http', `Intento de autenticación → ${response.status}`, { status: response.status, ms })
      return
    }

    //* Un 200 cuyo cuerpo dice `error: true` es un fallo disfrazado de éxito. El backend envuelve
    //* todo en `{ error, msg, data }`, y sin esta rama esos casos serían invisibles.
    const body: any = response.body
    if (body && typeof body === 'object' && body.error === true) {
      this.trace.push('http', `${request.method} ${path} → falló (${this.short(body.msg)})`, { method: request.method, path, status: response.status, ms, msg: this.short(body.msg), body: this.cuerpoDe(request) })
      return
    }

    const isMutation = request.method !== 'GET'
    if (!isMutation && ms < SLOW_GET_MS) return

    const label = isMutation ? `${request.method} ${path} → ${response.status}` : `${path} tardó ${(ms / 1000).toFixed(1)} s`
    this.trace.push('http', `${label}${isMutation ? ` · ${(ms / 1000).toFixed(1)} s` : ''}`, { method: request.method, path, status: response.status, ms, body: this.cuerpoDe(request) })
  }

  private recordFailure(request: HttpRequest<any>, err: HttpErrorResponse, path: string, isCredential: boolean, ms: number): void {
    if (isCredential) {
      this.trace.push('http', `Intento de autenticación → ${err.status}`, { status: err.status, ms })
      return
    }

    //! Lista BLANCA, no negra: del error se copia únicamente `err.error.msg` y solo si es texto.
    //! `err.error.data`, el cuerpo y los encabezados no se leen jamás. Que sea lista blanca es lo
    //! que hace que un campo nuevo del servidor no se filtre por omisión el día que aparezca.
    const msg = typeof err?.error?.msg === 'string' ? this.short(err.error.msg) : ''
    const reason = err.status === 0 ? 'sin respuesta del servidor' : this.short(err.statusText) || 'error'

    this.trace.push('http', `${request.method} ${path} → ${err.status} (${reason}) · ${(ms / 1000).toFixed(1)} s`, {
      method: request.method,
      path,
      status: err.status,
      ms,
      msg,
      body: this.cuerpoDe(request),
    })
  }

  private short(text: unknown, max = 140): string {
    return typeof text === 'string' ? text.replace(/\s+/g, ' ').trim().slice(0, max) : ''
  }
}
